import { ChangeDetectionStrategy, Component, effect, EventEmitter, input, Input, InputSignal, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { PredictedCostDto } from '../../../interfaces/reports/predicted-cost-dto';

import { NgClass } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ApexAxisChartSeries, ApexChart, ApexLegend, ApexOptions, ApexPlotOptions, ApexTitleSubtitle, ApexXAxis, ApexYAxis, NgApexchartsModule } from 'ng-apexcharts';
import { ChartCardComponent } from '../chart-card/chart-card.component';
import { BaseDashboardChartComponent, FilterOption } from '../base-dashboard-chart.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'co-box-candle-plot',
  imports: [SelectModule, FormsModule, NgApexchartsModule, ChartCardComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './box-candle-plot.component.html',
  styleUrl: './box-candle-plot.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BoxCandlePlotComponent extends BaseDashboardChartComponent implements OnInit {
  data: InputSignal<PredictedCostDto> = input.required();
  chartOptions = signal<Partial<BoxCandleChartOptions>>({});
  chartFilters = signal<FilterOption[][]>([]);
  chartReady = input.required<boolean>();
  @Output() categoryPicked = new EventEmitter<string>();

  private langChangeSub?: Subscription;
  constructor(private translate: TranslateService) {

    super();
     this.langChangeSub = this.translate.onLangChange.subscribe(() => {
    this.getChartOptions();
  });
    effect(() => {
      const d = this.data()
      if (d)
        this.getChartOptions();
    })
  }

  ngOnDestroy() {
      this.langChangeSub?.unsubscribe();
  }

  ngOnInit() {
    this.getChartOptions()
  }

  getChartOptions() {
    const d = this.data();
    if (!d || !d.actualData?.length) return;

    const actualSeries = d.actualData
      .filter(item => item.category)
      .map(item => ({
        y: [item.min, item.q1, item.median, item.q3, item.max],
        x: this.wrapCategoryLabel(item.category!),
        category: item.category!
      }));

    const predictedSeries = d.predictedData
      .filter(item => item.category)
      .map(item => ({
        y: [item.min, item.q1, item.median, item.q3, item.max],
        x: this.wrapCategoryLabel(item.category!),
        category: item.category!
      }));

    this.chartOptions.set({
      series: [
      {
        name: this.translate.instant('BOXPLOT.ACTUAL_COST'),
        type: 'boxPlot',
        data: actualSeries,
      },
      {
        name: this.translate.instant('BOXPLOT.PREDICTED_COST'),
        type: 'boxPlot',
        data: predictedSeries,
      },
    ],
      chart: {
        type: "boxPlot",
        width: '100%',
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: false,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: { color: '#90CAF9', opacity: 0.4 },
            stroke: { color: '#0D47A1', opacity: 0.4, width: 1 },
          },
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: false, reset: true },
        }, events: {
          mounted: (chartContext: { el: HTMLElement }) => this.applyBoxColors(chartContext.el),
          updated: (chartContext: { el: HTMLElement }) => this.applyBoxColors(chartContext.el)
        },
      } as ApexChart,
      plotOptions: {
        boxPlot: {
          distributed: true,
          colors: {
            upper: 'rgba(0,0,0,0)',   // same color = solid box
            lower: 'rgba(0,0,0,0)'
          }
        } as any
      },
      stroke: {
        width: 2,
        colors: ['#125773', '#7978E9'], // series 0 (Current), series 1 (Historical)
      },
      colors: ['#125773', '#7978E9'],
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: -45,
          hideOverlappingLabels: false,
          style: {
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
            colors: '#333'
          },
          trim: false,
          maxHeight: 120
        }
      },
      yaxis: {
        min: 0,
        labels: {
          formatter: (val: number) => this.formatNumberShort(val)
        },
        title: {
        }
      },
       tooltip: {
      enabled: true,
      shared: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const translate = this.translate;
        const hoveredData = w.config.series[seriesIndex].data[dataPointIndex];
        const category = hoveredData.category ?? this.getCategoryText(hoveredData.x);
        const seriesData = w.config.series.map((chartSeries: any) => ({
          name: chartSeries.name,
          data: chartSeries.data.find((item: any) => (item.category ?? this.getCategoryText(item.x)) === category),
        }));
        const formatSeries = (chartSeries: { name: string; data: any }) => chartSeries.data
          ? `
            <strong>${chartSeries.name}:</strong><br/>
            ${translate.instant('BOXPLOT_TOOLTIP.MIN')}: ${this.formatBoxPlotValue(chartSeries.data.y[0])}<br/>
            ${translate.instant('BOXPLOT_TOOLTIP.Q1')}: ${this.formatBoxPlotValue(chartSeries.data.y[1])}<br/>
            ${translate.instant('BOXPLOT_TOOLTIP.MEDIAN')}: ${this.formatBoxPlotValue(chartSeries.data.y[2])}<br/>
            ${translate.instant('BOXPLOT_TOOLTIP.Q3')}: ${this.formatBoxPlotValue(chartSeries.data.y[3])}<br/>
            ${translate.instant('BOXPLOT_TOOLTIP.MAX')}: ${this.formatBoxPlotValue(chartSeries.data.y[4])}
          `
          : '';

        return `
          <div style="padding:10px; font-size:12px; background:white; border:1px solid #ccc;">
            <strong>${translate.instant('BOXPLOT_TOOLTIP.CATEGORY')}:</strong> ${category}<br/>
            ${seriesData.map(formatSeries).join('<hr style="margin:6px 0;"/>')}
          </div>
        `;
      }
  },
      legend: {
        show: true,
        position: 'bottom',
        floating: true,
        offsetY: 30
        , markers: {
          fillColors: ['rgba(7, 62, 107, 0.6)', 'rgba(76, 8, 88, 0.6)']
        },
        itemMargin: {
          horizontal: 5,
          vertical: 5,
        }
      },
    });
  }

  private formatBoxPlotValue(value: unknown): string {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '-';
  }

  private wrapCategoryLabel(category: string): string | string[] {
    const trimmedCategory = category.trim();
    const hyphenIndex = trimmedCategory.indexOf('-');

    if (hyphenIndex > 0 && hyphenIndex < trimmedCategory.length - 1) {
      const beforeHyphen = trimmedCategory.slice(0, hyphenIndex).trim();
      const afterHyphen = trimmedCategory.slice(hyphenIndex + 1).trim();

      return [
        `${beforeHyphen} -`,
        afterHyphen
      ];
    }

    const words = trimmedCategory.split(/\s+/).filter(Boolean);
    if (words.length < 2) return trimmedCategory;

    const splitIndex = Math.ceil(words.length / 2);
    return [
      words.slice(0, splitIndex).join(' '),
      words.slice(splitIndex).join(' ')
    ];
  }

  private getCategoryText(category: string | string[]): string {
    return Array.isArray(category) ? category.join(' ') : category;
  }

  getColors(color: string = '') {
    if (color == '') {
      return [
        '#4B49AC',
        '#98BDFF',
        '#7DA0FA',
        '#7978E9',
        '#125773',
        '#27CDF2',
        '#488A99',
        '#C6D936'
      ];
    } else return [color];
  }

  applyBoxColors(containerEl: HTMLElement) {
    const seriesGroups = Array.from(containerEl.querySelectorAll<SVGGElement>('.apexcharts-series'));

    const colors = [
      { fill: 'rgba(7, 62, 107, 0.6)', stroke: '#125773' }, // Actual
      { fill: 'rgba(76, 8, 88, 0.6)', stroke: '#7978E9' }   // Predicted
    ];

    seriesGroups.forEach((seriesG, index) => {

      seriesG.querySelectorAll<SVGRectElement>('rect, path').forEach(el => {
        el.setAttribute('fill', colors[index]?.fill || 'transparent');
        el.setAttribute('stroke', colors[index]?.stroke || 'transparent');
      });
      seriesG.querySelectorAll<SVGLineElement>('line').forEach(line => {
        line.setAttribute('stroke', colors[index]?.stroke || 'transparent');
      });
    });

    const xLabels = containerEl.querySelectorAll('.apexcharts-xaxis text');
    if (xLabels.length === 1 && seriesGroups.length === 2) {
      const boxWidth = seriesGroups[0].getBBox().width;
      const totalWidth = seriesGroups[0].getBBox().width + seriesGroups[1].getBBox().width;
      const offset = (totalWidth / 2) - (boxWidth / 2);
      seriesGroups.forEach(seriesG => {
        const transform = seriesG.getAttribute('transform') || '';
        seriesG.setAttribute('transform', `${transform} translate(${-offset}, 0)`);
      });
    }
  }

  formatNumberShort(n: number): string {
    if (typeof n !== 'number') {
      return n;
    }

    if (n >= 1000000000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (n >= 1000000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toString();
  }
}
export type BoxCandleChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  colors?: string[];
};
